import {
  Badge,
  Button,
  GridSection,
  GridSectionBody,
  GridSectionHeader,
  GridSectionItem,
  GridSectionSpan,
  Timeline,
} from "@components";
import { formatAmount, formatDateTime, formatSortCode } from "@utils";
import { CheckLg, XLg } from "react-bootstrap-icons";
import { BanknotesIcon, ReceiptRefundIcon } from "@heroicons/react/24/outline";
import { transactionStatusColors, refundStatusLabel } from "../../../common";
import LinkedTransactionItem from "./LinkedTransactionItem";
import { reconciledTimelineEntry } from "./reconciledTimelineEntry";

// Builds the lifecycle activity feed for a single refund (initialised, then its
// terminal state: cancelled / refunded / failed). The Timeline sorts by date.
const mapRefundTimeline = (refund, reconciledLabel, reconciledByName) => {
  const events = [
    {
      title: "Refund initialised",
      subTitle: `${formatDateTime(refund.issuesOn)}${refund.issuedBy ? ` - ${refund.issuedBy}` : ""}`,
      date: refund.issuesOn,
      nodeContent: { icon: ReceiptRefundIcon },
    },
  ];

  const label = refundStatusLabel(refund.status);

  if (label === "Cancelled" && refund.canceledOn) {
    events.push({
      title: "Refund cancelled",
      subTitle: formatDateTime(refund.canceledOn),
      date: refund.canceledOn,
      nodeContent: { icon: XLg, color: "red" },
    });
  } else if (label === "Refunded" && refund.settledOn) {
    events.push({
      title: "Refunded",
      subTitle: formatDateTime(refund.settledOn),
      amount: formatAmount(refund.amount),
      date: refund.settledOn,
      nodeContent: { icon: CheckLg, color: "success" },
    });
  } else if (label === "Failed") {
    events.push({
      title: "Refund failed",
      subTitle: refund.settledOn ? formatDateTime(refund.settledOn) : undefined,
      date: refund.settledOn ?? refund.issuesOn,
      nodeContent: { icon: XLg, color: "red" },
    });
  }

  return [...events, ...reconciledTimelineEntry(reconciledLabel, reconciledByName)];
};

const RefundDetails = ({ payment, refundId, paymentMethodKey, onNavigate, onEdit, reconciledLabel, reconciledByName }) => {
  const refund = payment.refunds?.find((r) => r.id === refundId);

  return (
    <>
      <GridSection>
        <GridSectionSpan>
          <h2 className="text-3xl font-medium m-0 mr-2">
            {formatAmount(refund ? refund.amount : payment.amount)}
          </h2>
          {refund && (
            <Badge
              color={transactionStatusColors[refundStatusLabel(refund.status)]}
            >
              {refundStatusLabel(refund.status)}
            </Badge>
          )}
        </GridSectionSpan>
        <GridSectionItem>
          <GridSectionHeader>Customer</GridSectionHeader>
          <GridSectionBody>{payment.customerName}</GridSectionBody>
        </GridSectionItem>
      </GridSection>

      {refund && (
        <>
          <div className="w-full flex items-center px-5 pt-5">
            <p className="font-semibold m-0">Refund details</p>
          </div>
          <GridSection className="grid-cols-2">
            {refund.accountHolderName && (
              <GridSectionItem>
                <GridSectionHeader>Payee</GridSectionHeader>
                <GridSectionBody>{refund.accountHolderName}</GridSectionBody>
              </GridSectionItem>
            )}
            {refund.reference && (
              <GridSectionItem>
                <GridSectionHeader>Reference</GridSectionHeader>
                <GridSectionBody>{refund.reference}</GridSectionBody>
              </GridSectionItem>
            )}
            {refund.accountNumber && (
              <GridSectionItem>
                <GridSectionHeader>Account number</GridSectionHeader>
                <GridSectionBody>{refund.accountNumber}</GridSectionBody>
              </GridSectionItem>
            )}
            {refund.sortCode && (
              <GridSectionItem>
                <GridSectionHeader>Sort code</GridSectionHeader>
                <GridSectionBody>{formatSortCode(refund.sortCode)}</GridSectionBody>
              </GridSectionItem>
            )}
            <GridSectionItem>
              <GridSectionHeader>Refund created by</GridSectionHeader>
              <GridSectionBody>{refund.issuedBy ?? "—"}</GridSectionBody>
            </GridSectionItem>
            <GridSectionItem>
              <GridSectionHeader>Refund created on</GridSectionHeader>
              <GridSectionBody>{formatDateTime(refund.issuesOn)}</GridSectionBody>
            </GridSectionItem>
            {refund.canceledOn ? (
              <GridSectionItem>
                <GridSectionHeader>Cancelled on</GridSectionHeader>
                <GridSectionBody>
                  {formatDateTime(refund.canceledOn)}
                </GridSectionBody>
              </GridSectionItem>
            ) : refund.settledOn ? (
              <GridSectionItem>
                <GridSectionHeader>Refunded on</GridSectionHeader>
                <GridSectionBody>
                  {formatDateTime(refund.settledOn)}
                </GridSectionBody>
              </GridSectionItem>
            ) : null}
            {onEdit && refund.status === "Initialized" && (
              <GridSectionSpan>
                <Button color="link" onClick={() => onEdit(refund)}>
                  Edit
                </Button>
              </GridSectionSpan>
            )}
          </GridSection>
        </>
      )}

      <div className="w-full flex items-center px-5 pt-5">
        <p className="font-semibold m-0">Linked payment</p>
      </div>
      <LinkedTransactionItem
        reference={payment.reference}
        linkLabel="View payment"
        icon={BanknotesIcon}
        statusLabel={payment.status}
        statusColor={transactionStatusColors[payment.status]}
        amount={payment.amount}
        date={payment.paymentDate}
        onClick={
          onNavigate
            ? () =>
                onNavigate({
                  transactionType: "Payment",
                  paymentId: payment.id,
                  refundId: null,
                  paymentMethodKey,
                })
            : undefined
        }
      />

      {refund && (
        <div className="border-t border-zinc-200">
          <Timeline activity={mapRefundTimeline(refund, reconciledLabel, reconciledByName)} />
        </div>
      )}
    </>
  );
};

export default RefundDetails;
